import { Injectable } from '@angular/core';
import { catchError, map, tap } from 'rxjs/internal/operators';
import { Resident } from '../model/resident';
import { Observable, of, Subject, throwError } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ApiRoutes } from 'src/app/config/api-routes';
import { ApplicationConfig } from '../../../config/application.config';
import { AppDocument } from '../model/app-document';
import { ResidentFunction } from '../model/resident-function';
import { ResidentStructure } from '../model/resident-structure';
import { Notification } from '../model/notification';
import { Document } from '../model/document';
import { ResidentContact } from '../model/resident-contact';
import { PaginationResponse } from '../model/pagination-response';

@Injectable()
export class ResidentsService {

  public updated$: Subject<any> = new Subject<any>();

  constructor(private httpClient: HttpClient, private apiRoutes: ApiRoutes, private applicationConfig: ApplicationConfig) {
  }

  public getResidents(httpParams?: HttpParams, pageNumber?: number, pageSize?: number): Observable<PaginationResponse<Resident>> {
    let params = (httpParams ?? new HttpParams())
    if (pageNumber)
      params = params.append('pageNumber', pageNumber.toString());
    if (pageSize)
      params = params.append('pageSize', pageSize.toString());
    return this.httpClient.get<PaginationResponse<Resident>>(this.apiRoutes.RESIDENTS, {params: params}).pipe(
      map(response => {
        response.currentElements.map(resident => {
          resident.thumbnailUrl = (resident.thumbnailUrl != null) ? resident.thumbnailUrl : this.applicationConfig.AVATAR_PLACEHOLDER;
          return resident;
        });
        return response;
      }),
      catchError(error => {
        return of(new PaginationResponse<Resident>([], -1, -1));
      })
    );
  }

  public getResident(contactId: number, application: string): Observable<Resident> {
    return this.httpClient.get<Resident>(
      this.apiRoutes.getRouteWithInjectedParams(this.apiRoutes.RESIDENT_DETAILS, [application, contactId.toString()])
    ).pipe(
      map(resident => {
        resident.thumbnailUrl = (resident.thumbnailUrl != null) ? resident.thumbnailUrl : this.applicationConfig.AVATAR_PLACEHOLDER;
        return resident;
      }),
      catchError(error => {
        return of(null);
      })
    );
  }

  public inviteResident(contactId: number, application: string): Observable<any> {
    return this.httpClient.get(
      this.apiRoutes.getRouteWithInjectedParams(this.apiRoutes.RESIDENT_INVITE, [application, contactId.toString()])
    ).pipe(
      tap((() => this.updated$.next(null))),
      catchError(error => {
        return throwError(null);
      })
    );
  }

  public inviteResidents(residentContacts: ResidentContact[]): Observable<any> {
    return this.httpClient.post(this.apiRoutes.RESIDENT_INVITE_MULTIPLE, { residentContacts }).pipe(
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  getDocuments(application: string, contactId: number): Observable<AppDocument[]> {
    return this.httpClient.get<AppDocument[]>(this.apiRoutes.getRouteWithInjectedParams(
      this.apiRoutes.RESIDENT_DOCUMENTS, [application, contactId.toString()])).pipe(
        catchError(error => {
          return throwError(null);
        })
      );
  }

  getStructures(application: string, contactId: number): Observable<ResidentStructure[]> {
    return this.httpClient.get<ResidentStructure[]>(this.apiRoutes.getRouteWithInjectedParams(
      this.apiRoutes.RESIDENT_STRUCTURES, [application, contactId.toString()])).pipe(
        catchError(error => {
          return throwError(null);
        })
      );
  }

  getLocations(application: string, contactId: number): Observable<ResidentFunction[]> {
    return this.httpClient.get<ResidentFunction[]>(this.apiRoutes.getRouteWithInjectedParams(
      this.apiRoutes.RESIDENT_LOCATIONS, [application, contactId.toString()])).pipe(
        catchError(error => {
          return throwError(null);
        })
      );
  }

  getNotifications(application: string, contactId: number): Observable<Notification[]> {
    return this.httpClient.get<Notification[]>(this.apiRoutes.getRouteWithInjectedParams(
      this.apiRoutes.RESIDENT_NOTIFICATIONS, [application, contactId.toString()])).pipe(
        catchError(error => {
          return throwError(null);
        })
      );
  }

  getBuildingDocuments(application: string, contactId: number): Observable<Document[]> {
    return this.httpClient.get<Document[]>(this.apiRoutes.getRouteWithInjectedParams(
      this.apiRoutes.RESIDENT_BUILDING_DOCUMENTS, [application, contactId.toString()])).pipe(
        catchError(error => {
          return throwError(null);
        })
      );
  }

  public changeContactDocumentVisibility(application: string, contactDocumentId: number, visibleForResidents: boolean): Observable<AppDocument> {
    return this.httpClient
      .post(this.apiRoutes.getRouteWithInjectedParams(
        this.apiRoutes.RESIDENT_DOCUMENT_UPDATE_VISIBILITY,
        [application, contactDocumentId.toString()]), { visibleForResidents }
      )
      .pipe(
        catchError(error => {
          return of(null);
        })
      );
  }
}
